import { Injectable, signal } from '@angular/core';
import { EnumObjectExtended } from '@card-software/library';
import { ItemEnumsProxyService } from '../proxies/item/item-enums.proxy.service';
import { Observable } from 'rxjs';
import { LocaleHelperService } from './localehelperservice.service';
import { ResponseTranslation } from '../models/Translations';

@Injectable({
  providedIn: 'root',
})
export class EnumService {
  private readonly itemEnumLocalStorageKey = 'itemEnums';

  /** Signal that increments each time enums are loaded. Effects calling getEnum() will re-run automatically. */
  readonly enumsVersion = signal(0);

  private isLoading = false;

  constructor(
    private readonly itemEnumsProxyService: ItemEnumsProxyService,
    private localHelperService: LocaleHelperService,
  ) {}

  public loadEnums(): Observable<boolean> {
    this.isLoading = true;
    return new Observable<boolean>((observer) => {
      this.itemEnumsProxyService.getAll().subscribe({
        next: (response) => {
          localStorage.setItem(this.itemEnumLocalStorageKey, JSON.stringify(response));
          this.isLoading = false;
          this.enumsVersion.update((v) => v + 1);
          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading enums', error);
          this.isLoading = false;
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  public getEnum(enumName: string, enumType: 'itemEnums' = 'itemEnums'): EnumObjectExtended {
    // Access signal so effects that call getEnum() re-run when enums are loaded
    this.enumsVersion();

    const raw = localStorage.getItem(enumType);
    if (!raw) {
      if (!this.isLoading) {
        this.loadEnums().subscribe();
      }
      return { name: enumName, values: {}, valuesEnglish: {} };
    }
    const enumObj = JSON.parse(raw);
    const found = enumObj.find(
      (x: { name: string; values: Record<string, string>; valuesEnglish?: Record<string, string> }) => x.name === enumName,
    );

    if (!found) {
      if (!this.isLoading) {
        this.loadEnums().subscribe();
      }
      return { name: enumName, values: {}, valuesEnglish: {} };
    }
    const formattedValues = Object.entries(found.values).reduce(
      (acc, [key, val]) => {
        let localizedValue = this.localHelperService.getLocalizedValue(val as unknown as ResponseTranslation);

        if (enumName === 'Illumination') {
          if (localizedValue === 'D65') {
            localizedValue = 'D-65';
          } else if (localizedValue === 'INCA_A10') {
            localizedValue = 'INCA (A10)';
          }
        }

        acc[key] = localizedValue;
        return acc;
      },
      {} as Record<string, string>,
    );

    const englishValues: Record<string, string> = {};
    if (found.valuesEnglish) {
      for (const key in found.valuesEnglish) {
        englishValues[key] = found.valuesEnglish[key];
      }
    } else {
      for (const key in found.values) {
        const value = found.values[key];
        englishValues[key] = typeof value === 'object' && value.en ? value.en : value;
      }
    }

    return {
      name: found.name,
      values: formattedValues,
      valuesEnglish: englishValues,
    };
  }
}
